import { FunctionComponent, useState } from 'react';

import Box from '../../Layout/Box/Box';
import Icon from '../Icon/Icon';
import SubTitle from '../../Text/SubTitle/SubTitle';

import { getWidget } from './widgets';
import type { WidgetData, WidgetName, WidgetMode } from './types';

import styles from './Widget.module.scss';

export type WidgetProps = {
  name: WidgetName;
  data: WidgetData;
  mode?: WidgetMode;
  labelSize?: 'sm' | 'md' | 'lg';
  height?: number;
  showDetails?: boolean;
  id?: number;
  onClick?: (id: number) => void;
};

type FC = FunctionComponent<WidgetProps>;

const Widget: FC = (props: WidgetProps) => {
  const {
    name,
    data,
    mode = 'preview',
    labelSize = 'md',
    height = 200,
    showDetails = false,
    id = 0,
    onClick = () => {},
  } = props;

  const [currentPanel, setCurrentPanel] = useState(1);
  const [disabledNavIcon, setDisabledNavIcon] = useState<'left' | 'right' | null>('left');

  const { panelCount, title, widget } = getWidget(name, mode, { data, currentPanel, showDetails, height });

  function handleLeftNavClick() {
    const targetPanel = currentPanel - 1;

    if (targetPanel < 1) return;

    if (targetPanel === 1) {
      setCurrentPanel(targetPanel);
      setDisabledNavIcon('left');

      return;
    }

    setCurrentPanel(targetPanel);
    setDisabledNavIcon(panelCount > 2 ? null : 'right');
  }

  function handleRightNavClick() {
    const targetPanel = currentPanel + 1;

    if (targetPanel > panelCount) return;

    if (targetPanel === panelCount) {
      setCurrentPanel(targetPanel);
      setDisabledNavIcon('right');

      return;
    }

    setCurrentPanel(targetPanel);
    setDisabledNavIcon(panelCount > 2 ? null : 'left');
  }

  return (
    <div
      id={`widget-${name}-${mode}`}
      className={[styles.container, styles[getLabelSizeClass()]].join(' ')}
      onClick={() => onClick(id)}
      role="presentation"
    >
      <Box ai="center" jc="sb">
        <SubTitle subTitle={title} />
        {panelCount > 1 && mode === 'hero' && (
          <Box>
            <div
              id="widget-navLeft"
              className={styles[getNavIconClass('left')]}
              onClick={handleLeftNavClick}
              role="button"
              aria-hidden
            >
              <Icon size="xsm" icon="angleLeft" color={getNavIconColor('left')} />
            </div>
            <div
              id="widget-navRight"
              className={styles[getNavIconClass('right')]}
              onClick={handleRightNavClick}
              role="button"
              aria-hidden
            >
              <Icon size="xsm" icon="angleRight" color={getNavIconColor('right')} />
            </div>
          </Box>
        )}
      </Box>
      {widget}
    </div>
  );

  function getLabelSizeClass() {
    return `label-${labelSize}`;
  }

  function getNavIconClass(navIcon: 'left' | 'right') {
    return disabledNavIcon !== navIcon ? 'iconButton' : '';
  }

  function getNavIconColor(navIcon: 'left' | 'right') {
    return disabledNavIcon !== navIcon ? 'primary-dark' : 'medium-light';
  }
};

export type { ToWidgetData, WidgetData, WidgetName, WidgetMode } from './types';
export default Widget;
